/**
 * Subscription dialog and Settings card body (C27; D-261, D-030; engineer decision D-267).
 *
 * The dialog keeps the old `MonthlySubscriptionGateModal` frame (original/src/components/chat/
 * MonthlySubscriptionGateModal.tsx:95-366): dark slate panel with the two glows, the header with the shield and
 * the "verified licence" chip, the status banner (expired / grace / active, :142-189), the licence card (:192-228),
 * the features block (:231-244) and the activation form (:247-323), the support strip (:326-346) and the footer
 * (:350-365). What changes (V-54 – V-56): every figure comes from the server (no client licence, D-133); the
 * banner follows the five D-030 states; the licence card shows the price list, the balance, the period and the
 * cover end instead of the invented "terminal id" / "capacity tier"; the features block lists today's charge;
 * the duration selector is gone (a card carries its own value); the code is confirmed with the Super Manager's
 * PIN (the container's PIN dialog); the support strip has no invented phone numbers; a history list is added.
 */
import { AlertTriangle, Building2, CalendarClock, CheckCircle2, CreditCard, History, Key, Layers, Lock, RefreshCw, ShieldCheck, Sparkles, Wallet, X, Zap } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";
import { useT } from "../i18n-context.js";

export type SubscriptionState = "active" | "grace_download" | "grace_readonly" | "closed" | "blocked";

export interface SubscriptionView {
  readonly officeName: string;
  readonly state: SubscriptionState;
  /** Formatted by the container (office date). */
  readonly coverEndsOn: string | null;
  readonly daysLeft: number | null;
  readonly lapseDay: number | null;
  readonly nextState: { readonly state: SubscriptionState; readonly on: string } | null;
  readonly unlimited: boolean;
  readonly priceListName: string | null;
  readonly balance: string | null; // formatted with its currency
  readonly period: { readonly from: string; readonly to: string; readonly users: number; readonly branches: number } | null;
  readonly upcomingPeriods: number;
  readonly dailyLines: readonly { readonly label: string; readonly amount: string }[];
  readonly dailyTotal: string | null;
  readonly usage: { readonly users: number; readonly branches: number };
  readonly lockedMinutes: number | null;
}

export interface SubscriptionHistoryRow {
  readonly at: string;
  readonly label: string;
  readonly amount?: string | undefined;
  readonly tone: "plus" | "minus" | "neutral" | "warn";
}

export interface SubscriptionModalProps {
  readonly view: SubscriptionView | null;
  readonly loading?: boolean;
  /** Super Manager only; others see the state and a hint. */
  readonly canRedeem: boolean;
  /** Resolves with an error message to show, or null on success. */
  readonly onRedeem: (code: string) => Promise<string | null>;
  readonly history: readonly SubscriptionHistoryRow[];
  /** D-030 closed / blocked: the dialog is the payment page and cannot be closed. */
  readonly isGateLock?: boolean;
  readonly onClose: () => void;
  readonly onLogout?: () => void;
}

const BANNER: Record<SubscriptionState, "ok" | "warn" | "bad"> = { active: "ok", grace_download: "warn", grace_readonly: "warn", closed: "bad", blocked: "bad" };

export function SubscriptionModal({ view, loading, canRedeem, onRedeem, history, isGateLock = false, onClose, onLogout }: SubscriptionModalProps) {
  const t = useT();
  const [inputCode, setInputCode] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!inputCode.trim()) {
      setErrorMsg(t("subscription.code_required"));
      return;
    }
    setBusy(true);
    setErrorMsg("");
    setSuccessMsg("");
    const err = await onRedeem(inputCode.trim());
    setBusy(false);
    if (err === null) {
      setSuccessMsg(t("subscription.redeemed"));
      setInputCode("");
    } else if (err) setErrorMsg(err);
  };

  const tone = view ? BANNER[view.state] : "ok";
  const locked = view?.lockedMinutes ?? null;

  return (
    <div data-testid="subscription-modal" className="fixed inset-0 z-70 flex items-center justify-center p-3 sm:p-5 bg-black/75 backdrop-blur-md animate-in fade-in duration-200">
      <div className="bg-[#0f172a] text-slate-100 border border-slate-700/80 rounded-3xl shadow-2xl overflow-hidden w-full max-w-2xl flex flex-col relative max-h-[92vh]">
        <div className="absolute -top-24 -right-24 w-72 h-72 bg-indigo-500/15 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl pointer-events-none"></div>

        {/* Header (:104-136) */}
        <div className="px-6 py-5 bg-slate-900/90 border-b border-slate-800 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3.5">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400/20 via-indigo-500/20 to-blue-500/30 border border-amber-400/30 flex items-center justify-center text-amber-400 shrink-0 shadow-lg shadow-amber-400/5">
              <ShieldCheck size={26} className="animate-pulse" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base sm:text-lg font-black text-white tracking-wide">{t("subscription.title")}</h3>
                <span className="px-2.5 py-0.5 rounded-full text-[11px] font-black bg-amber-400/10 text-amber-300 border border-amber-400/30 flex items-center gap-1">
                  <Sparkles size={11} />
                  <span>{t("subscription.verified")}</span>
                </span>
              </div>
              <p className="text-xs text-slate-400 font-medium mt-0.5">{t("subscription.subtitle")}</p>
            </div>
          </div>
          {!isGateLock && (
            <button type="button" data-testid="subscription-close" onClick={onClose} className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-slate-800 transition-colors">
              <X size={20} />
            </button>
          )}
        </div>

        <div className="p-6 overflow-y-auto space-y-5 custom-scrollbar">
          {loading || !view ? (
            <div className="flex items-center justify-center py-10 text-slate-400"><RefreshCw size={20} className="animate-spin" /></div>
          ) : (
            <>
              {/* Status banner (:142-189), per D-030 state */}
              {tone === "bad" ? (
                <div data-testid="subscription-banner" data-state={view.state} className="p-4 rounded-2xl bg-rose-950/50 border border-rose-600/50 text-rose-200 flex items-start gap-3">
                  <AlertTriangle size={22} className="text-rose-400 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-black text-sm text-rose-100">{t(`subscription.state.${view.state}`)}</h4>
                    <p className="text-xs text-rose-300/90 mt-1 leading-relaxed">{t(`subscription.state_text.${view.state}`, { day: view.lapseDay ?? 0 })}</p>
                    {view.nextState && <p className="text-[11px] text-rose-300/80 mt-1 font-bold">{t("subscription.next_state", { state: t(`subscription.state.${view.nextState.state}`), date: view.nextState.on })}</p>}
                  </div>
                </div>
              ) : tone === "warn" ? (
                <div data-testid="subscription-banner" data-state={view.state} className="p-4 rounded-2xl bg-amber-950/50 border border-amber-600/50 text-amber-200 flex items-start gap-3">
                  <AlertTriangle size={22} className="text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-black text-sm text-amber-100">{t(`subscription.state.${view.state}`)}</h4>
                    <p className="text-xs text-amber-300/90 mt-1 leading-relaxed">{t(`subscription.state_text.${view.state}`, { day: view.lapseDay ?? 0 })}</p>
                    {view.nextState && <p className="text-[11px] text-amber-300/80 mt-1 font-bold">{t("subscription.next_state", { state: t(`subscription.state.${view.nextState.state}`), date: view.nextState.on })}</p>}
                  </div>
                </div>
              ) : (
                <div data-testid="subscription-banner" data-state={view.state} className="p-4 rounded-2xl bg-emerald-950/40 border border-emerald-500/40 text-emerald-200 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 size={24} className="text-emerald-400 shrink-0" />
                    <div>
                      <h4 className="font-black text-sm text-emerald-100">{t("subscription.state.active")}</h4>
                      <p className="text-xs text-emerald-300/80 font-bold mt-0.5">
                        {view.unlimited ? t("subscription.unlimited") : t("subscription.cover_until", { date: view.coverEndsOn ?? "—", n: view.daysLeft ?? 0 })}
                      </p>
                    </div>
                  </div>
                  <span className="px-3 py-1 rounded-xl bg-emerald-500/20 text-emerald-300 text-xs font-black border border-emerald-500/30">{t("subscription.active_chip")}</span>
                </div>
              )}

              {/* Licence card (:192-228) */}
              <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 space-y-3">
                <Row icon={<Building2 size={14} className="text-indigo-400" />} label={t("subscription.entity")} value={<span className="font-bold text-slate-200">{view.officeName}</span>} />
                <Row icon={<Key size={14} className="text-amber-400" />} label={t("subscription.price_list")} value={<span className="font-bold text-amber-300 bg-amber-400/10 px-2 py-0.5 rounded border border-amber-400/20">{view.priceListName ?? t("subscription.not_priced")}</span>} />
                <Row icon={<Wallet size={14} className="text-emerald-400" />} label={t("subscription.balance")} value={<span data-testid="subscription-balance" dir="ltr" className="font-mono text-slate-200">{view.balance ?? "—"}</span>} />
                <Row icon={<CalendarClock size={14} className="text-blue-400" />} label={t("subscription.period")} value={
                  view.period ? (
                    <span className="font-bold text-slate-300">{t("subscription.period_value", { from: view.period.from, to: view.period.to, users: view.period.users, branches: view.period.branches })}</span>
                  ) : (
                    <span className="text-slate-500">{t("subscription.no_period")}</span>
                  )
                } />
                <Row last icon={<Layers size={14} className="text-blue-400" />} label={t("subscription.usage")} value={<span className="font-bold text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded border border-emerald-400/20">{t("subscription.usage_value", { users: view.usage.users, branches: view.usage.branches })}</span>} />
              </div>

              {/* Today's charge in the old features block (:231-244) */}
              <div className="p-4 rounded-2xl bg-slate-900/50 border border-slate-800">
                <h5 className="text-xs font-black text-slate-300 mb-2.5 flex items-center gap-1.5">
                  <Zap size={14} className="text-amber-400" />
                  {t("subscription.daily_title")} {view.dailyTotal && <span dir="ltr" className="font-mono text-amber-300">{view.dailyTotal}</span>}
                </h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] text-slate-300">
                  {view.dailyLines.length === 0 ? (
                    <div className="flex items-center gap-2"><CheckCircle2 size={13} className="text-emerald-400 shrink-0" /><span>{t("subscription.daily_none")}</span></div>
                  ) : (
                    view.dailyLines.map((l, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle2 size={13} className="text-emerald-400 shrink-0" />
                        <span>{l.label}</span>
                        <span dir="ltr" className="font-mono text-slate-400 ms-auto">{l.amount}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Activation form (:247-323) */}
              {canRedeem ? (
                <form onSubmit={(e) => void submit(e)} className="p-4 rounded-2xl bg-gradient-to-b from-indigo-950/30 to-slate-900 border border-indigo-500/30 space-y-3.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-black text-indigo-200 flex items-center gap-1.5">
                      <CreditCard size={14} className="text-amber-400" />
                      <span>{t("subscription.redeem_label")}</span>
                    </label>
                    {locked !== null && <span className="text-[11px] font-bold text-rose-300 flex items-center gap-1"><Lock size={12} />{t("subscription.locked", { n: locked })}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      data-testid="subscription-code"
                      dir="ltr"
                      autoComplete="off"
                      spellCheck={false}
                      value={inputCode}
                      maxLength={24}
                      onChange={(e) => {
                        setInputCode(e.target.value.toUpperCase());
                        setErrorMsg("");
                        setSuccessMsg("");
                      }}
                      placeholder="XXXX-XXXX-XXXX-XXXX"
                      className="flex-1 bg-slate-900 border border-slate-700 rounded-xl px-4 py-2.5 text-xs sm:text-sm font-mono font-bold text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    />
                    <button
                      type="submit"
                      data-testid="subscription-redeem"
                      disabled={busy || !inputCode.trim() || locked !== null}
                      className={`px-5 py-2.5 rounded-xl font-black text-xs transition-all flex items-center gap-2 shrink-0 ${
                        busy || !inputCode.trim() || locked !== null
                          ? "bg-slate-800 text-slate-500 cursor-not-allowed"
                          : "bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 shadow-lg shadow-amber-500/20 active:scale-95 cursor-pointer"
                      }`}
                    >
                      {busy ? <RefreshCw size={14} className="animate-spin" /> : <ShieldCheck size={16} />}
                      <span>{t("subscription.redeem")}</span>
                    </button>
                  </div>
                  {errorMsg && <p data-testid="subscription-error" className="text-xs font-bold text-rose-400 bg-rose-950/40 p-2.5 rounded-xl border border-rose-800/40">{errorMsg}</p>}
                  {successMsg && <p data-testid="subscription-success" className="text-xs font-bold text-emerald-400 bg-emerald-950/40 p-2.5 rounded-xl border border-emerald-800/40">{successMsg}</p>}
                </form>
              ) : (
                <div className="p-4 rounded-2xl bg-slate-900/60 border border-slate-800 text-xs text-slate-300 font-bold">{t("subscription.ask_manager")}</div>
              )}

              {/* History (new) */}
              <div className="p-3.5 rounded-2xl bg-slate-900/60 border border-slate-800/80 text-xs">
                <button type="button" onClick={() => setShowHistory((s) => !s)} className="w-full flex items-center justify-between text-slate-300 font-bold">
                  <span className="flex items-center gap-1.5"><History size={14} className="text-indigo-400" />{t("subscription.history")}</span>
                  <span className="text-slate-500">{showHistory ? "−" : "+"}</span>
                </button>
                {showHistory && (
                  <div className="mt-3 space-y-1.5 max-h-56 overflow-y-auto custom-scrollbar">
                    {history.length === 0 && <p className="text-slate-500">{t("subscription.history_empty")}</p>}
                    {history.map((h, i) => (
                      <div key={i} className="flex items-center justify-between gap-3 border-b border-slate-800/80 pb-1.5">
                        <span className="text-slate-500 font-mono text-[10px] shrink-0">{h.at}</span>
                        <span className={`flex-1 ${h.tone === "warn" ? "text-amber-300" : "text-slate-300"}`}>{h.label}</span>
                        {h.amount && <span dir="ltr" className={`font-mono ${h.tone === "plus" ? "text-emerald-400" : h.tone === "minus" ? "text-rose-300" : "text-slate-400"}`}>{h.amount}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Support strip (:326-346) without invented numbers */}
              <div className="p-3.5 rounded-2xl bg-slate-900/60 border border-slate-800/80 text-xs text-slate-400">
                <span className="font-bold text-slate-300">{t("subscription.support_title")}</span>
                <p className="text-[11px] text-slate-500 mt-0.5">{t("subscription.support_text")}</p>
              </div>
            </>
          )}
        </div>

        {/* Footer (:350-365) */}
        <div className="px-6 py-4 bg-slate-900 border-t border-slate-800 flex items-center justify-between text-xs text-slate-400 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-ping"></div>
            <span className="font-medium">{t("subscription.footer")}</span>
          </div>
          {isGateLock ? (
            onLogout && (
              <button type="button" data-testid="subscription-logout" onClick={onLogout} className="px-4 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold transition-colors cursor-pointer">
                {t("subscription.logout")}
              </button>
            )
          ) : (
            <button type="button" onClick={onClose} className="px-4 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold transition-colors cursor-pointer">
              {t("subscription.close")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ icon, label, value, last }: { icon: ReactNode; label: string; value: ReactNode; last?: boolean }) {
  return (
    <div className={`flex items-center justify-between text-xs ${last ? "" : "pb-2 border-b border-slate-800"}`}>
      <span className="text-slate-400 flex items-center gap-1.5">
        {icon}
        {label}
      </span>
      {value}
    </div>
  );
}

/** Settings card body "الاشتراك" (new card, V-54): the state at a glance and the button that opens the dialog. */
export function SubscriptionCardBody({ state, summary, onOpen }: { state: SubscriptionState | null; summary: string; onOpen: () => void }) {
  const t = useT();
  const cls =
    state === "active" || state === null
      ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20"
      : state === "closed" || state === "blocked"
        ? "bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-500/20"
        : "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/20";
  return (
    <div className="space-y-3">
      <div data-testid="subscription-card-state" className={`p-4 rounded-xl border text-sm font-black ${cls}`}>
        <div>{state ? t(`subscription.state.${state}`) : t("subscription.loading")}</div>
        <div className="text-xs font-bold mt-1 opacity-80">{summary}</div>
      </div>
      <button type="button" data-testid="subscription-open" onClick={onOpen} className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-black flex items-center justify-center gap-2 transition-colors">
        <CreditCard size={16} />
        {t("subscription.open")}
      </button>
    </div>
  );
}
